// src/components/ui/GinoriButton.tsx
import React from 'react';
import { Button, ButtonProps } from '@mantine/core';

interface GinoriButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  elegant?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export function GinoriButton({ 
  variant = 'primary', 
  elegant = false,
  children,
  onClick,
  ...props 
}: GinoriButtonProps) {
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--mantine-color-ginoriGold-7)',
          color: 'var(--mantine-color-white)',
          border: '1px solid var(--mantine-color-ginoriGold-7)',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-ginoriGold-8)',
            borderColor: 'var(--mantine-color-ginoriGold-8)',
          },
        };
      case 'secondary':
        return {
          backgroundColor: 'var(--mantine-color-ginoriBlue-7)',
          color: 'var(--mantine-color-white)',
          border: '1px solid var(--mantine-color-ginoriBlue-7)',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-ginoriBlue-8)',
            borderColor: 'var(--mantine-color-ginoriBlue-8)',
          },
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'var(--mantine-color-ginoriBlue-8)',
          border: '1px solid var(--mantine-color-ginoriBlue-5)',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-ginoriBlue-1)',
            borderColor: 'var(--mantine-color-ginoriBlue-7)',
            color: 'var(--mantine-color-ginoriBlue-8)',
          },
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'var(--mantine-color-ginoriBlue-8)',
          border: '1px solid transparent',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-ginoriNeutral-2)',
            borderColor: 'var(--mantine-color-ginoriNeutral-4)',
          },
        };
      default:
        return {};
    }
  };

  return (
    <Button
      radius="xs"
      size="md"
      onClick={onClick}
      style={{
        fontFamily: elegant ? '"Playfair Display", serif' : 'var(--font-family-secondary)',
        fontWeight: elegant ? 400 : 400,
        letterSpacing: elegant ? '0.5px' : '0.025em',
        textTransform: 'none',
        transition: 'all 0.2s ease',
        ...getVariantStyles(),
        '&:hover': {
          transform: 'translateY(-1px)',
          ...getVariantStyles()['&:hover'],
        },
      }}
      {...props}
    >
      {children}
    </Button>
  );
}
